/**
 * Cross-Window State Synchronization for Multi-Window Electron App
 * 
 * Uses BroadcastChannel API to sync state between multiple windows
 * and IPC for component transfers.
 */

import { componentsMapType, CrossWindowMessage, DisplayInfo, MultiWindowState, VirtualBounds, WindowInfo } from '@/src/shared/contracts/app';

const CHANNEL_NAME = 'nvzero-component-sync';
const STATE_CHANNEL_NAME = 'nvzero-state-sync';
const AUTH_CHANNEL_NAME = 'nvzero-auth-sync';

// Singleton channel instances
let componentChannel: BroadcastChannel | null = null;
let stateChannel: BroadcastChannel | null = null;
let authChannel: BroadcastChannel | null = null;

// Window state
let currentWindowId: string | null = null;
let currentDisplayId: number | null = null;

/**
 * Initialize cross-window sync
 */
export function initCrossWindowSync(windowId: string, displayId: number): void {
  currentWindowId = windowId;
  currentDisplayId = displayId;
  
  if (typeof BroadcastChannel !== 'undefined') {
    componentChannel = new BroadcastChannel(CHANNEL_NAME);
    stateChannel = new BroadcastChannel(STATE_CHANNEL_NAME);
    authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    
    console.log(`[CrossWindowSync] Initialized for window ${windowId} on display ${displayId}`);
  } else {
    console.warn('[CrossWindowSync] BroadcastChannel not available');
  }
}

/**
 * Get current window ID
 */
export function getWindowId(): string | null {
  return currentWindowId;
}

/**
 * Get current display ID
 */
export function getDisplayId(): number | null {
  return currentDisplayId;
}

/**
 * Parse window ID from URL params (for initial load)
 */
export function parseWindowIdFromUrl(): { windowId: string | null; displayId: number | null } {
  if (typeof window === 'undefined') return { windowId: null, displayId: null };
  
  const params = new URLSearchParams(window.location.search);
  const windowId = params.get('windowId');
  const displayIdStr = params.get('displayId');
  const displayId = displayIdStr ? parseInt(displayIdStr, 10) : null;
  
  return { windowId, displayId };
}

/**
 * Broadcast component update to other windows
 */
export function broadcastComponentUpdate(
  type: CrossWindowMessage['type'],
  payload: any
): void {
  if (!componentChannel || !currentWindowId) return;
  
  const message: CrossWindowMessage = {
    type,
    sourceWindowId: currentWindowId,
    payload,
    timestamp: Date.now()
  };
  
  componentChannel.postMessage(message);
}

/**
 * Subscribe to component messages from other windows
 */
export function onComponentMessage(
  callback: (message: CrossWindowMessage) => void
): () => void {
  if (!componentChannel) return () => {};
  
  const handler = (event: MessageEvent<CrossWindowMessage>) => {
    // Ignore messages from self
    if (event.data.sourceWindowId === currentWindowId) return;
    callback(event.data);
  };
  
  componentChannel.addEventListener('message', handler);
  
  return () => {
    componentChannel?.removeEventListener('message', handler);
  };
}

/**
 * Broadcast state sync to other windows
 */
export function broadcastStateSync(components: componentsMapType[]): void {
  if (!stateChannel || !currentWindowId) return;
  
  const message: CrossWindowMessage = {
    type: 'STATE_SYNC',
    sourceWindowId: currentWindowId,
    payload: { components },
    timestamp: Date.now()
  };
  
  stateChannel.postMessage(message);
}

/**
 * Subscribe to state sync from other windows
 */
export function onStateSync(
  callback: (components: componentsMapType[], sourceWindowId: string) => void
): () => void {
  if (!stateChannel) return () => {};
  
  const handler = (event: MessageEvent<CrossWindowMessage>) => {
    if (event.data.sourceWindowId === currentWindowId) return;
    if (event.data.type === 'STATE_SYNC') {
      callback(event.data.payload.components, event.data.sourceWindowId);
    }
  };
  
  stateChannel.addEventListener('message', handler);
  
  return () => {
    stateChannel?.removeEventListener('message', handler);
  };
}

/**
 * Cleanup cross-window sync
 */
export function cleanupCrossWindowSync(): void {
  componentChannel?.close();
  stateChannel?.close();
  authChannel?.close();
  componentChannel = null;
  stateChannel = null;
  authChannel = null;
  currentWindowId = null;
  currentDisplayId = null;
}

// ============================================
// AUTHENTICATION SYNC
// ============================================

export type AuthEventType = 'LOGOUT' | 'LOGIN' | 'SESSION_REFRESH';

export interface AuthSyncMessage {
  type: AuthEventType;
  sourceWindowId: string;
  timestamp: number;
}

/**
 * Get the current window's ID for auth broadcasts
 * Uses currentWindowId if set, otherwise falls back to URL params
 */
function getWindowIdForBroadcast(): string {
  if (currentWindowId) return currentWindowId;
  if (typeof window === 'undefined') return 'ssr';
  const params = new URLSearchParams(window.location.search);
  const urlWindowId = params.get('windowId');
  return urlWindowId || 'unknown';
}

/**
 * Broadcast logout event to all windows
 * All windows should sign out when any window signs out
 */
export function broadcastLogout(): void {
  if (typeof BroadcastChannel === 'undefined') return;
  
  // Create a temporary channel if not initialized (e.g., on landing page)
  const channel = authChannel || new BroadcastChannel(AUTH_CHANNEL_NAME);
  
  const sourceWindowId = getWindowIdForBroadcast();
  const message: AuthSyncMessage = {
    type: 'LOGOUT',
    sourceWindowId,
    timestamp: Date.now()
  };
  
  console.log('[CrossWindowSync] Broadcasting logout to all windows, sourceWindowId:', sourceWindowId);
  channel.postMessage(message);
  
  // Close temporary channel if we created one
  if (!authChannel) {
    channel.close();
  }
}

/**
 * Broadcast login event to all windows
 * Other windows should refresh their auth state
 */
export function broadcastLogin(): void {
  if (typeof BroadcastChannel === 'undefined') return;
  
  const channel = authChannel || new BroadcastChannel(AUTH_CHANNEL_NAME);
  
  const sourceWindowId = getWindowIdForBroadcast();
  const message: AuthSyncMessage = {
    type: 'LOGIN',
    sourceWindowId,
    timestamp: Date.now()
  };
  
  console.log('[CrossWindowSync] Broadcasting login to all windows, sourceWindowId:', sourceWindowId);
  channel.postMessage(message);
  
  if (!authChannel) {
    channel.close();
  }
}

/**
 * Broadcast session refresh event
 * Used when session token is refreshed
 */
export function broadcastSessionRefresh(): void {
  if (typeof BroadcastChannel === 'undefined') return;
  
  const channel = authChannel || new BroadcastChannel(AUTH_CHANNEL_NAME);
  
  const sourceWindowId = getWindowIdForBroadcast();
  const message: AuthSyncMessage = {
    type: 'SESSION_REFRESH',
    sourceWindowId,
    timestamp: Date.now()
  };
  
  console.log('[CrossWindowSync] Broadcasting session refresh to all windows, sourceWindowId:', sourceWindowId);
  channel.postMessage(message);
  
  if (!authChannel) {
    channel.close();
  }
}

/**
 * Subscribe to auth events from other windows
 * Note: This filters out messages from the same window to prevent infinite loops
 */
export function onAuthEvent(
  callback: (event: AuthSyncMessage) => void
): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => {};
  
  // Create a channel for listening (works even if initCrossWindowSync wasn't called)
  const channel = authChannel || new BroadcastChannel(AUTH_CHANNEL_NAME);
  const isTemporary = !authChannel;
  
  // Get window ID to filter out self-messages
  // Use URL params as fallback since currentWindowId might not be set yet
  const getMyWindowId = (): string | null => {
    if (currentWindowId) return currentWindowId;
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('windowId');
  };
  
  const handler = (event: MessageEvent<AuthSyncMessage>) => {
    const myWindowId = getMyWindowId();
    
    // IMPORTANT: Ignore messages from self to prevent infinite loops
    // If we can't determine our window ID, still process (for web mode)
    if (myWindowId && event.data.sourceWindowId === myWindowId) {
      console.log('[CrossWindowSync] Ignoring auth event from self:', event.data.type);
      return;
    }
    
    console.log('[CrossWindowSync] Received auth event from another window:', event.data.type);
    callback(event.data);
  };
  
  channel.addEventListener('message', handler);
  
  return () => {
    channel.removeEventListener('message', handler);
    if (isTemporary) {
      channel.close();
    }
  };
}

// ============================================
// MULTI-WINDOW ELECTRON API HELPERS
// ============================================

/**
 * Check if running in Electron
 */
export function isElectron(): boolean {
  return typeof window !== 'undefined' && !!(window as any).electronAPI?.isElectron;
}

/**
 * Get all displays
 */
export async function getAllDisplays(): Promise<any[]> {
  if (!isElectron()) return [];
  return (window as any).electronAPI.getAllDisplays();
}

/**
 * Get virtual bounds spanning all displays
 */
export async function getVirtualBounds(): Promise<VirtualBounds | null> {
  if (!isElectron()) return null;
  return (window as any).electronAPI.getVirtualBounds();
}

/**
 * Get global cursor position
 */
export async function getGlobalCursorPosition(): Promise<{ x: number; y: number } | null> {
  if (!isElectron()) return null;
  return (window as any).electronAPI.getGlobalCursorPosition();
}

/**
 * Get current window info
 */
export async function getWindowInfo(): Promise<WindowInfo | null> {
  if (!isElectron()) return null;
  return (window as any).electronAPI.getWindowInfo();
}

/**
 * Get all open windows
 */
export async function getAllWindows(): Promise<WindowInfo[]> {
  if (!isElectron()) return [];
  return (window as any).electronAPI.getAllWindows();
}

/**
 * Create a new window on a specific display
 */
export async function createWindowOnDisplay(displayId: number): Promise<string | null> {
  if (!isElectron()) return null;
  return (window as any).electronAPI.createWindowOnDisplay(displayId);
}

/**
 * Create a new window on the next available display
 */
export async function createNewWindow(preferredDisplayId?: number): Promise<{
  windowId: string;
  displayId: number;
  bounds: { x: number; y: number; width: number; height: number };
} | null> {
  if (!isElectron()) return null;
  return (window as any).electronAPI.createNewWindow(preferredDisplayId ?? null);
}

/**
 * Check if this is the main/primary window
 * 
 * Returns TRUE if:
 * - Not running in Electron (web mode - always main)
 * - No windowId param in URL (first/default window)
 * - windowId starts with "main-"
 * 
 * Returns FALSE only if windowId exists and starts with "window-" (extension window)
 */
export function isMainWindow(): boolean {
  if (typeof window === 'undefined') return true; // SSR - assume main
  
  // Not in Electron = web mode = main window
  if (!isElectron()) return true;
  
  const params = new URLSearchParams(window.location.search);
  const windowId = params.get('windowId');
  
  // No windowId means this is the first/main window
  // (secondary windows ALWAYS get explicit windowId params)
  if (!windowId) return true;
  
  // Explicit main window
  if (windowId.startsWith('main-')) return true;
  
  // Explicit extension window
  if (windowId.startsWith('window-')) return false;
  
  // Unknown - default to main
  return true;
}

/**
 * Transfer a component to another window
 */
export async function transferComponent(data: {
  component: componentsMapType;
  components?: componentsMapType[];
  sourceWindowId: string;
  targetWindowId: string;
  globalPosition: { x: number; y: number };
}): Promise<boolean> {
  if (!isElectron()) return false;
  return (window as any).electronAPI.transferComponent(data);
}

export async function acknowledgeComponentTransfer(data: {
  transferId: string;
  accepted: boolean;
  error?: string;
}): Promise<boolean> {
  if (!isElectron()) return false;
  return (window as any).electronAPI.acknowledgeComponentTransfer(data);
}

/**
 * Check what window is at a global position
 */
export async function checkWindowAtGlobalPosition(pos: { x: number; y: number }): Promise<{
  inWindow: boolean;
  windowId?: string;
  bounds?: { x: number; y: number; width: number; height: number };
  isSameWindow?: boolean;
  display?: any;
  shouldCreateWindow?: boolean;
} | null> {
  if (!isElectron()) return null;
  return (window as any).electronAPI.checkWindowAtGlobalPosition(pos);
}

/**
 * Notify other windows that a drag has started
 */
export async function notifyDragStart(data: {
  component: componentsMapType;
  globalPosition: { x: number; y: number };
}): Promise<void> {
  if (!isElectron()) return;
  await (window as any).electronAPI.notifyDragStart(data);
}

/**
 * Notify other windows that a drag has ended
 */
export async function notifyDragEnd(componentId: string): Promise<void> {
  if (!isElectron()) return;
  await (window as any).electronAPI.notifyDragEnd({ componentId });
}

/**
 * Subscribe to component received events (from transfers)
 */
export function onComponentReceived(
  callback: (data: {
    transferId: string;
    component: componentsMapType;
    components?: componentsMapType[];
    sourceWindowId: string;
    targetWindowId?: string;
    globalPosition: { x: number; y: number };
  }) => void
): () => void {
  if (!isElectron()) return () => {};
  return (window as any).electronAPI.onComponentReceived(callback);
}

/**
 * Subscribe to component transfer complete events
 */
export function onComponentTransferComplete(
  callback: (data: {
    componentId: string;
    componentIds?: string[];
    targetWindowId: string;
  }) => void
): () => void {
  if (!isElectron()) return () => {};
  return (window as any).electronAPI.onComponentTransferComplete(callback);
}

/**
 * Subscribe to cross-window messages
 */
export function onCrossWindowMessage(
  callback: (message: CrossWindowMessage) => void
): () => void {
  if (!isElectron()) return () => {};
  return (window as any).electronAPI.onCrossWindowMessage(callback);
}

/**
 * Subscribe to window info updates
 */
export function onWindowInfo(
  callback: (info: WindowInfo & { virtualBounds?: VirtualBounds; isMainWindow?: boolean }) => void
): () => void {
  if (!isElectron()) return () => {};
  return (window as any).electronAPI.onWindowInfo(callback);
}

export function onDisplaysChanged(
  callback: (data: { displays: DisplayInfo[]; virtualBounds: VirtualBounds }) => void
): () => void {
  if (!isElectron()) return () => {};
  return (window as any).electronAPI.onDisplaysChanged(callback);
}
